import { calculateMeterValue } from '../../market-conditions/z-score/calculate-meter-value';
import {
  DataType,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../../types';
import {
  getLatestYoYGrowth,
  getYoYGrowthAt,
  inferYoYPeriodOffsetFromDateRefs,
} from '../../utils/salary-growth-yoy';
import {
  type CompositionV3AnyGraphqlResponse,
  isCompositionV3FullMetrics,
} from './composition-v3-graphql-response';

type CompositionEntityV3 = NonNullable<
  CompositionV3AnyGraphqlResponse['entities']
>[number];

const buildSalaryValuesAlignedToMonthsV3 = (
  entity: CompositionEntityV3,
  monthsLength: number
): number[] => {
  const values = new Array(monthsLength).fill(0);
  const metrics = isCompositionV3FullMetrics(entity.metrics)
    ? entity.metrics
    : undefined;
  const salaryData = metrics?.salary ?? [];
  for (const point of salaryData) {
    const idx = point.id;
    if (typeof idx === 'number' && idx >= 0 && idx < monthsLength) {
      values[idx] = point.val;
    }
  }
  return values;
};

const getSalaryGrowthForEntityV3 = (
  entity: CompositionEntityV3,
  months: string[]
): number | null => {
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const values = buildSalaryValuesAlignedToMonthsV3(entity, months.length);
  const yoyGrowth = getLatestYoYGrowth(values, periodOffset);
  if (yoyGrowth !== null) {
    return yoyGrowth;
  }

  const metrics = isCompositionV3FullMetrics(entity.metrics)
    ? entity.metrics
    : undefined;
  const salaryData = metrics?.salary ?? [];
  if (salaryData.length < 2) {
    return null;
  }

  const firstPoint = salaryData[0];
  const lastPoint = salaryData[salaryData.length - 1];
  if (!firstPoint || !lastPoint) {
    return null;
  }
  const firstValue = firstPoint.val;
  const lastValue = lastPoint.val;

  if (firstValue === 0) {
    return null;
  }

  return (lastValue - firstValue) / firstValue;
};

export const convertCompositionToWagePressureV3 = ({
  data,
  monthRefs,
  variant = 'value',
}: {
  data: CompositionV3AnyGraphqlResponse;
  monthRefs: string[];
  variant?: 'value' | 'percentile';
}): SingleDataSet => {
  const compositionEntities = data.entities ?? [];
  const months = monthRefs;
  const resultData: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const id = entity.id;
    const growthValue = getSalaryGrowthForEntityV3(entity, months);

    if (growthValue === null) {
      continue;
    }

    const value =
      variant === 'percentile'
        ? (calculateMeterValue({
            value: growthValue,
            metric: 'wageGrowth',
          }) ?? 0)
        : growthValue;

    const dim = entity.short_name ?? entity.long_name ?? 'Selected';

    resultData.push({
      dim,
      value,
      id: String(id),
    });
  }

  return { type: DataType.SINGLE, data: resultData };
};

export function convertCompositionToSalaryGrowthV3({
  data,
  chartType,
  monthRefs,
}: {
  data: CompositionV3AnyGraphqlResponse;
  chartType: string;
  monthRefs: string[];
}): SingleDataSet | TimeseriesDataSet {
  const months = monthRefs;
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);

  if (chartType === 'line') {
    const compositionEntities = data.entities ?? [];
    const raw: TimeseriesData[] = [];

    for (const entity of compositionEntities) {
      const dim = entity.short_name ?? entity.long_name ?? 'Selected';
      const values = buildSalaryValuesAlignedToMonthsV3(entity, months.length);
      const lineValues: TimeseriesDataPoint[] = [];
      for (let idx = 0; idx < months.length; idx++) {
        const dateStr = months[idx] ?? '';
        if (!dateStr) {
          continue;
        }
        const growthValue = getYoYGrowthAt(values, idx, periodOffset);
        lineValues.push({ date: dateStr, value: growthValue });
      }
      if (
        lineValues.length === 0 ||
        lineValues.every((v) => v.value === null)
      ) {
        continue;
      }
      raw.push({
        dim,
        timeseries: lineValues,
        id: String(entity.id),
      });
    }

    if (raw.length === 0) {
      return { type: DataType.TIMESERIES, data: [] };
    }

    const firstIndexWithValue = (() => {
      const len = raw[0]?.timeseries.length ?? 0;
      for (let i = 0; i < len; i++) {
        const hasValue = raw.some(
          (s) =>
            s.timeseries[i]?.value !== null &&
            s.timeseries[i]?.value !== undefined
        );
        if (hasValue) {
          return i;
        }
      }
      return 0;
    })();

    const trimmed: TimeseriesData[] = raw.map((s) => ({
      dim: s.dim,
      timeseries: s.timeseries.slice(firstIndexWithValue),
      ...(s.id !== undefined ? { id: s.id } : {}),
    }));

    return { type: DataType.TIMESERIES, data: trimmed };
  }

  const compositionEntities = data.entities ?? [];
  const resultData: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const values = buildSalaryValuesAlignedToMonthsV3(entity, months.length);
    const growthValue = getLatestYoYGrowth(values, periodOffset);
    const dim = entity.short_name ?? entity.long_name ?? 'Selected';

    resultData.push({
      dim,
      value: growthValue,
      id: String(entity.id),
    });
  }

  return { type: DataType.SINGLE, data: resultData };
}
